import React from "react";
import { BsInfoCircle, BsWallet, BsShieldCheck, BsPlusCircle, BsTrash, BsLightning, BsPeople, BsExclamationTriangle, BsLockFill } from "react-icons/bs";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useAccount } from "wagmi";
import { useSelector, useDispatch } from "react-redux";
import { selectGeneral, setShowWalletRequiredModal } from "../../redux/slices/generalSlice";
import useWalletAuth from "../../hooks/useWalletAuth";
import { getChain } from "../../utils/currencies";

const Step5AdvancedSettings = ({ formik, userWalletAddress, isPublished }) => {
  const { openConnectModal } = useConnectModal();
  const { isConnected } = useAccount();
  const { hasLinkedWallet, isLoggedIn } = useWalletAuth();
  const dispatch = useDispatch();

  const handleAddAttribute = () => {
    const currentAttributes = formik.values.attributes || [];
    formik.setFieldValue("attributes", [
      ...currentAttributes,
      { trait_type: "", value: "" },
    ]);
  };

  const handleRemoveAttribute = (index) => {
    const currentAttributes = [...(formik.values.attributes || [])];
    currentAttributes.splice(index, 1);
    formik.setFieldValue("attributes", currentAttributes);
  };

  const handleAttributeChange = (index, field, value) => {
    const currentAttributes = [...(formik.values.attributes || [])];
    currentAttributes[index] = { ...currentAttributes[index], [field]: value };
    formik.setFieldValue("attributes", currentAttributes);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="text-center mb-6 md:mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-purple-100 to-blue-100 mb-3">
          <BsShieldCheck className="text-2xl md:text-3xl text-primary" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
          Advanced Settings
        </h2>
        <p className="text-sm md:text-base text-gray-600">
          Configure blockchain, royalties, and NFT metadata
        </p>
      </div>

      {isPublished && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <BsLockFill className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Some settings are locked.</span> Minting mode, royalties, and royalty recipient are set on-chain and cannot be changed after publishing. You can still add or edit NFT attributes.
          </p>
        </div>
      )}

      {/* Minting Mode Selection */}
      <div className={isPublished ? "opacity-60" : ""}>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <span className="flex items-center gap-2">
            <BsLightning className="text-primary" />
            Minting Mode *
            {isPublished && <BsLockFill className="text-amber-400 text-xs" />}
          </span>
        </label>
        <p className="text-xs text-gray-500 mb-4">
          Choose how membership NFTs will be distributed to your members
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Option 1: Members Mint (Default) */}
          <button
            type="button"
            onClick={() => !isPublished && formik.setFieldValue("preMintMode", false)}
            disabled={isPublished}
            className={`p-4 md:p-5 rounded-xl border-2 text-left transition-all ${
              !formik.values.preMintMode
                ? "border-primary bg-gradient-to-br from-purple-50 to-blue-50 shadow-md"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            } ${isPublished ? "cursor-not-allowed" : ""}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                !formik.values.preMintMode 
                  ? "bg-primary text-white" 
                  : "bg-gray-100 text-gray-500"
              }`}>
                <BsPeople className="text-xl" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-gray-800">Members Mint</h4>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    Recommended
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Members mint their own NFT when they join and pay
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li className="flex items-center gap-1.5">
                    <span className="text-green-500">✓</span> Lower upfront gas for creator
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="text-green-500">✓</span> Members pay their own gas fees
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="text-green-500">✓</span> Better for larger communities
                  </li>
                </ul>
              </div>
              {!formik.values.preMintMode && (
                <svg className="w-5 h-5 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </button>

          {/* Option 2: Pre-Mint Mode */}
          <button
            type="button"
            onClick={() => !isPublished && formik.setFieldValue("preMintMode", true)}
            disabled={isPublished}
            className={`p-4 md:p-5 rounded-xl border-2 text-left transition-all ${
              formik.values.preMintMode
                ? "border-primary bg-gradient-to-br from-purple-50 to-blue-50 shadow-md"
                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
            } ${isPublished ? "cursor-not-allowed" : ""}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                formik.values.preMintMode 
                  ? "bg-primary text-white" 
                  : "bg-gray-100 text-gray-500"
              }`}>
                <BsLightning className="text-xl" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-gray-800">Pre-Mint</h4>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                    Advanced
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  You mint NFTs upfront, members pay to claim them
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li className="flex items-center gap-1.5">
                    <span className="text-blue-500">✓</span> Instant delivery to members
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="text-blue-500">✓</span> Better control over distribution
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="text-orange-500">⚠</span> Higher upfront gas cost
                  </li>
                </ul>
              </div>
              {formik.values.preMintMode && (
                <svg className="w-5 h-5 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </button>
        </div>

        {formik.values.preMintMode && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-2">
              <BsInfoCircle className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">⚠️ Two wallet transactions required</p>
                <ul className="text-xs space-y-1 list-disc list-inside">
                  <li><strong>Transaction 1:</strong> Deploy your community NFT contract on-chain.</li>
                  <li><strong>Transaction 2:</strong> Batch-mint all NFTs to your wallet (so members can claim them).</li>
                </ul>
                <p className="text-xs mt-2">
                  Both transactions must be approved. If you reject the second one, members will not be able to join. You can retry the minting step later by editing and re-publishing the community.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Royalties */}
      <div className={`bg-gradient-to-br from-purple-50 via-blue-50 to-purple-50 rounded-xl md:rounded-2xl p-4 md:p-6 border border-purple-100 ${isPublished ? "opacity-60" : ""}`}>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              Royalties (Secondary Sales)
              {isPublished && <BsLockFill className="text-amber-400 text-xs" />}
            </label>
            <p className="text-xs text-gray-500">
              Percentage you receive when NFTs are resold
            </p>
          </div>
          <div className="flex items-center gap-2 justify-center sm:justify-end">
            <input
              type="number"
              name="royalties"
              value={formik.values.royalties || 5}
              disabled={isPublished}
              onChange={(e) =>
                !isPublished && formik.setFieldValue(
                  "royalties",
                  Math.min(25, Math.max(0, parseFloat(e.target.value) || 0))
                )
              }
              min={0}
              max={25}
              step={0.5}
              className={`w-16 md:w-20 px-2 md:px-3 py-2 border border-gray-300 rounded-lg md:rounded-xl text-center font-semibold text-primary focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm ${isPublished ? "bg-gray-100 cursor-not-allowed" : ""}`}
            />
            <span className="text-gray-700 font-semibold text-lg">%</span>
          </div>
        </div>

        {/* Royalty Slider */}
        <input
          type="range"
          min={0}
          max={25}
          step={0.5}
          value={formik.values.royalties || 5}
          disabled={isPublished}
          onChange={(e) =>
            !isPublished && formik.setFieldValue("royalties", parseFloat(e.target.value))
          }
          className={`w-full h-2 bg-gray-200 rounded-lg appearance-none accent-primary ${isPublished ? "cursor-not-allowed" : "cursor-pointer"}`}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0%</span>
          <span>12.5%</span>
          <span>25%</span>
        </div>

        <div className="mt-4 flex items-start gap-2 text-sm text-gray-600">
          <BsInfoCircle className="text-primary flex-shrink-0 mt-0.5" />
          <p>
            Standard royalty is 5-10%. Higher royalties may discourage secondary sales.
          </p>
        </div>
      </div>

      {/* Royalty Recipient */}
      <div className={isPublished ? "opacity-60" : ""}>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          Royalty Recipient Wallet
          {isPublished && <BsLockFill className="text-amber-400 text-xs" />}
        </label>

        {/* Show wallet connection prompt if wallet not connected */}
        {!isConnected && !formik.values.royaltyRecipient && !isPublished && (
          <div className="mb-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <BsExclamationTriangle className="text-amber-600 text-xl flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-amber-800 font-medium mb-2">
                  Connect your wallet to automatically set the royalty recipient
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (!hasLinkedWallet && isLoggedIn) {
                      dispatch(setShowWalletRequiredModal({ 
                        show: true, 
                        action: "receive royalties from NFT sales"
                      }));
                    } else {
                      openConnectModal?.();
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
                >
                  <BsWallet className="text-lg" />
                  <span>{hasLinkedWallet ? "Connect Wallet" : "Link Wallet"}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="relative">
          <BsWallet className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            name="royaltyRecipient"
            value={formik.values.royaltyRecipient || userWalletAddress || ""}
            onChange={formik.handleChange}
            disabled={isPublished}
            placeholder={isConnected ? "Using connected wallet address" : "0x... (Connect wallet or enter manually)"}
            className={`w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm ${isPublished ? "bg-gray-50 cursor-not-allowed" : ""}`}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {userWalletAddress 
            ? "Using your connected wallet address. You can change it if needed."
            : "Connect your wallet or enter the address that should receive royalties"}
        </p>
      </div>

      {/* NFT Attributes */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">
              <span className="flex items-center gap-2">
                <BsPlusCircle className="text-primary" />
                NFT Attributes
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Add custom traits to your NFT metadata
            </p>
          </div>
          <button
            type="button"
            onClick={handleAddAttribute}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white hover:bg-purple-700 rounded-xl transition-all text-sm font-medium shadow-md hover:shadow-lg"
          >
            <BsPlusCircle className="text-lg" />
            <span>Add Attribute</span>
          </button>
        </div>

        {formik.values.attributes?.length > 0 ? (
          <div className="space-y-3">
            {formik.values.attributes.map((attr, index) => (
              <div key={index} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-xl">
                <input
                  type="text"
                  value={attr.trait_type}
                  onChange={(e) =>
                    handleAttributeChange(index, "trait_type", e.target.value)
                  }
                  placeholder="Trait name (e.g., Rarity)"
                  className="flex-1 px-3 md:px-4 py-2 md:py-2.5 border border-gray-200 rounded-lg md:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-sm"
                />
                <input
                  type="text"
                  value={attr.value}
                  onChange={(e) =>
                    handleAttributeChange(index, "value", e.target.value)
                  }
                  placeholder="Value (e.g., Legendary)"
                  className="flex-1 px-3 md:px-4 py-2 md:py-2.5 border border-gray-200 rounded-lg md:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-sm"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveAttribute(index)}
                  className="sm:w-auto px-4 py-2 md:py-2.5 text-red-500 hover:bg-red-50 rounded-lg md:rounded-xl transition-colors font-medium border border-red-200 hover:border-red-300 text-sm"
                >
                  <BsTrash className="inline mr-1" />
                  <span className="sm:hidden">Remove</span>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-xl text-gray-500">
            <p className="text-sm">No custom attributes added yet</p>
            <p className="text-xs mt-1">
              Default attributes (Category, Tier, etc.) will be included automatically
            </p>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="bg-gradient-to-br from-gray-50 to-purple-50 rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-200">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BsInfoCircle className="text-primary" />
          Configuration Summary
        </h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <div className="text-center p-3 md:p-4 bg-white rounded-xl shadow-sm border border-gray-100">
            <p className="text-xl md:text-2xl font-bold text-primary mb-1">
              {formik.values.totalSupply || 0}
            </p>
            <p className="text-xs text-gray-500">Total Supply</p>
          </div>
          <div className="text-center p-3 md:p-4 bg-white rounded-xl shadow-sm border border-gray-100">
            <p className="text-xl md:text-2xl font-bold text-primary mb-1">
              {formik.values.saleSettings?.price || 0}
            </p>
            <p className="text-xs text-gray-500">
              Price ({formik.values.saleSettings?.currency || "ETH"})
            </p>
          </div>
          <div className="text-center p-3 md:p-4 bg-white rounded-xl shadow-sm border border-gray-100">
            <p className="text-xl md:text-2xl font-bold text-primary mb-1">
              {formik.values.royalties || 5}%
            </p>
            <p className="text-xs text-gray-500">Royalties</p>
          </div>
          <div className="text-center p-3 md:p-4 bg-white rounded-xl shadow-sm border border-gray-100">
            <p className="text-xl md:text-2xl font-bold text-primary mb-1 capitalize truncate">
              {getChain({ network: formik.values.blockchain })?.shortName || formik.values.blockchain || "—"}
            </p>
            <p className="text-xs text-gray-500">Network</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step5AdvancedSettings;
